import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import * as S from './CollectionFormSelectDrawer.styles';
import { CollectionFormSelectOption } from './CollectionFormSelectOption';
import { Collection } from '@/components/AdForm/AdForm.types';
import { Drawer } from '@/components/Drawer/Drawer';
import { CloseIcon, SearchIcon } from '@/components/UI/Icons/Icons';
import { Property, PropertyOption } from '@/api/types/categories.types';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import {
  SearchInput,
  SearchInputRef,
} from '@/components/SearchInput/SearchInput';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { MAX_BOTTOM_SHEET_ITEMS } from '@/components/AdForm/components/CollectionFormSelect/CollectionFormSelectDrawer.constants';
import { NotFound } from '@/components/NotFoundPlaceholder/NotFoundPlaceholder.styles';

type Props = {
  isOpen: boolean;
  toggle: (v?: boolean) => void;
  propertyLegacyId?: string;
  options?: PropertyOption[];
  collection?: Collection;
  allProperties?: Property[];
  popularOptions?: PropertyOption[];
  onItemClick?: (option: PropertyOption) => void;
};

export const CollectionFormSelectDrawer: FC<Props> = ({
  isOpen,
  toggle,
  propertyLegacyId,
  options = [],
  collection,
  allProperties,
  popularOptions,
  onItemClick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<SearchInputRef>(null);
  const name = allProperties?.find(
    el => el.legacyResourceId === propertyLegacyId
  )?.name;

  useEffect(() => {
    searchInputRef.current?.clear();
  }, [isOpen]);

  function onOptionClick(value: PropertyOption) {
    if (onItemClick) {
      onItemClick(value);
    }
  }

  const list = options.filter(option =>
    option.name.toLowerCase().includes(query.toLowerCase())
  );

  const withPopularOptions = !!popularOptions?.length && !query;

  if (options?.length <= MAX_BOTTOM_SHEET_ITEMS) {
    return (
      <BottomSheet
        isOpen={isOpen}
        close={() => toggle(false)}
        title={name || ''}
        hasHeadBorder={false}
      >
        {options?.map(option => {
          return (
            <CollectionFormSelectOption
              key={option.legacyResourceId}
              option={option}
              collection={collection}
              onOptionClick={onOptionClick}
              appearance="radio"
              size="lg"
            />
          );
        })}
      </BottomSheet>
    );
  }

  return (
    <Drawer
      isAdaptive
      isOpen={isOpen}
      title={name}
      left={{
        icon: (
          <CloseIcon
            width={15}
            height={15}
            fill={theme.palette.primary.labels}
          />
        ),
        onClick: () => toggle(false),
      }}
      dataTestId={SearchResultsPageTestIds.collectionSelectDrawer}
      background={theme.palette.primary.background}
      hasBottomBorder={false}
      subtitle={
        <S.Container>
          <SearchInput
            ref={searchInputRef}
            onSearch={setQuery}
            leftIcon={<SearchIcon fill={theme.palette.secondary.labels} />}
            hasPadding={false}
            hasBorderBottom={false}
            customPaddingTop={12}
            customPaddingBottom={12}
          />
        </S.Container>
      }
    >
      {withPopularOptions && (
        <>
          <S.DrawerItemsTitle>{t('common.popular')}</S.DrawerItemsTitle>
          <S.Container>
            {popularOptions.map(option => {
              return (
                <CollectionFormSelectOption
                  key={option.legacyResourceId}
                  option={option}
                  collection={collection}
                  onOptionClick={onOptionClick}
                  appearance="radio"
                />
              );
            })}
          </S.Container>

          <S.DrawerItemsTitle>{t('common.all')}</S.DrawerItemsTitle>
        </>
      )}

      <S.Container>
        {list.length > 0 ? (
          list.map(option => {
            return (
              <CollectionFormSelectOption
                key={option.legacyResourceId}
                option={option}
                collection={collection}
                onOptionClick={onOptionClick}
                appearance="radio"
              />
            );
          })
        ) : (
          <NotFound>{t('common.nothing_found')}</NotFound>
        )}
      </S.Container>
    </Drawer>
  );
};

CollectionFormSelectDrawer.displayName = `CollectionFormSelectDrawer`;
